import React, { useState, useEffect } from 'react';
import { skillsMatrixService, employeeService, productService, productRevisionService } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Filter } from 'lucide-react';

const LEVELS = [
  { value: 0, label: '0 - Non abilitato', short: '0' },
  { value: 1, label: '1 - In affiancamento', short: '1' },
  { value: 2, label: '2 - Autonomo', short: '2' },
  { value: 3, label: '3 - Esperto', short: '3' },
];

const LEVEL_COLORS = {
  0: 'bg-slate-100 text-slate-500',
  1: 'bg-amber-100 text-amber-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-green-100 text-green-700',
};

const LEVEL_BG = {
  0: 'bg-slate-50',
  1: 'bg-amber-50',
  2: 'bg-blue-50',
  3: 'bg-green-50',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function SkillsMatrixPage() {
  const [tab, setTab] = useState('general');

  return (
    <div className="space-y-4" data-testid="skills-matrix-page">
      <h1 className="text-2xl font-bold text-slate-900" data-testid="skills-matrix-title">Matrix Competenze</h1>
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="matrix-tabs">
        <button onClick={() => setTab('general')} data-testid="tab-general"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'general' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          Competenze generali
        </button>
        <button onClick={() => setTab('product')} data-testid="tab-product"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'product' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          Competenze specifiche
        </button>
      </div>
      {tab === 'general' ? <GeneralMatrix /> : <ProductMatrix />}
    </div>
  );
}

function GeneralMatrix() {
  const [data, setData] = useState({ employees: [], skill_names: [] });
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filters, setFilters] = useState({ department: '', role: '' });
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.department) params.department = filters.department;
      if (filters.role) params.role = filters.role;
      const res = await skillsMatrixService.getGeneral(params);
      setData(res.data);
    } catch { toast.error('Errore caricamento matrice'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters]);
  useEffect(() => {
    Promise.all([employeeService.getDepartments(), employeeService.getRoles()])
      .then(([d, r]) => { setDepartments(d.data); setRoles(r.data); }).catch(() => {});
  }, []);

  const hasFilters = filters.department || filters.role;
  const skillNames = data.skill_names || [];
  const employees = data.employees || [];

  const getSkillLevel = (emp, skillName) => {
    const s = (emp.general_skills || []).find(gs => gs.skill_name === skillName);
    return s ? s.level : null;
  };

  return (
    <div className="space-y-4" data-testid="general-matrix">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Matrix Competenze Generali</h2>
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="general-matrix-filters-btn"
          className={hasFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : ''}>
          <Filter className="w-4 h-4 mr-1" />Filtri
        </Button>
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="general-matrix-filters">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Reparto</label>
              <select value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="gm-filter-department">
                <option value="">Tutti</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Ruolo</label>
              <select value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="gm-filter-role">
                <option value="">Tutti</option>
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          {hasFilters && <div className="mt-3 flex justify-end"><button onClick={() => setFilters({ department: '', role: '' })} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Resetta filtri</button></div>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : employees.length === 0 || skillNames.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nessun dato disponibile. Aggiungere competenze generali ai dipendenti.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 sticky left-0 bg-slate-50 z-10 min-w-[180px]">Dipendente</th>
                  {skillNames.map(name => (
                    <th key={name} className="px-3 py-3 text-center min-w-[110px]">
                      <span className="normal-case">{name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employees.map(emp => (
                  <tr key={emp.id} className="hover:bg-slate-50/50" data-testid={`gm-row-${emp.id}`}>
                    <td className="px-4 py-3 sticky left-0 bg-white z-10 font-medium text-slate-900 border-r border-slate-100">
                      <div>{emp.last_name} {emp.first_name}</div>
                      <div className="text-xs text-slate-400">{emp.department || ''}{emp.department && emp.role ? ' / ' : ''}{emp.role || ''}</div>
                    </td>
                    {skillNames.map(name => {
                      const level = getSkillLevel(emp, name);
                      return (
                        <td key={name} className={`px-3 py-3 text-center ${level !== null ? LEVEL_BG[level] : ''}`}>
                          {level !== null ? (
                            <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${LEVEL_COLORS[level]}`} title={LEVELS.find(l => l.value === level)?.label}>
                              {level}
                            </span>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
        {LEVELS.map(l => (
          <div key={l.value} className="flex items-center gap-1.5">
            <span className={`inline-block w-5 h-5 rounded-full text-center leading-5 font-semibold ${LEVEL_COLORS[l.value]}`}>{l.short}</span>
            <span>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductMatrix() {
  const [data, setData] = useState({ employees: [] });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [filters, setFilters] = useState({ product_id: '', revision_id: '' });

  useEffect(() => { productService.getAll().then(r => setProducts(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (filters.product_id) {
      productRevisionService.getAll({ product_id: filters.product_id }).then(r => setRevisions(r.data)).catch(() => setRevisions([]));
    } else { setRevisions([]); }
  }, [filters.product_id]);

  const load = async () => {
    if (!filters.product_id) { setData({ employees: [] }); return; }
    try {
      setLoading(true);
      const params = { product_id: filters.product_id };
      if (filters.revision_id) params.revision_id = filters.revision_id;
      const res = await skillsMatrixService.getProduct(params);
      setData(res.data);
    } catch { toast.error('Errore caricamento matrice'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters]);

  const selectedProduct = products.find(p => p.id === filters.product_id);

  return (
    <div className="space-y-4" data-testid="product-matrix">
      <h2 className="text-lg font-semibold text-slate-900">Matrix Competenze Specifiche</h2>

      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="product-matrix-filters">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Prodotto *</label>
            <select value={filters.product_id} onChange={e => setFilters({ product_id: e.target.value, revision_id: '' })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="pm-filter-product">
              <option value="">Seleziona prodotto</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Revisione (opzionale)</label>
            <select value={filters.revision_id} onChange={e => setFilters(f => ({ ...f, revision_id: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="pm-filter-revision" disabled={!filters.product_id}>
              <option value="">Tutte le revisioni</option>
              {revisions.map(r => <option key={r.id} value={r.id}>{r.revision_code}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {!filters.product_id ? (
          <div className="p-8 text-center text-slate-400 text-sm">Seleziona un prodotto per visualizzare le competenze specifiche.</div>
        ) : loading ? (
          <div className="p-8 text-center text-slate-400">Caricamento...</div>
        ) : (data.employees || []).length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nessun dipendente con competenze per {selectedProduct ? `${selectedProduct.code} - ${selectedProduct.name}` : 'questo prodotto'}.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Dipendente</th><th className="px-4 py-3">Livello competenza</th><th className="px-4 py-3">Data verifica</th><th className="px-4 py-3">Note</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(data.employees || []).map(emp => {
                  const skill = emp.skills?.[0];
                  return (
                    <tr key={emp.id} className="hover:bg-slate-50" data-testid={`pm-row-${emp.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{emp.last_name} {emp.first_name}</div>
                        <div className="text-xs text-slate-400">{emp.employee_code}{emp.department ? ` - ${emp.department}` : ''}</div>
                      </td>
                      <td className="px-4 py-3">
                        {skill ? <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${LEVEL_COLORS[skill.level]}`}>{LEVELS.find(l => l.value === skill.level)?.label || skill.level}</span> : '-'}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{skill ? fmtDate(skill.verification_date) : '-'}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{skill?.notes || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
        {LEVELS.map(l => (
          <div key={l.value} className="flex items-center gap-1.5">
            <span className={`inline-block w-5 h-5 rounded-full text-center leading-5 font-semibold ${LEVEL_COLORS[l.value]}`}>{l.short}</span>
            <span>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
